import { SafeAreaView, StyleSheet } from 'react-native';

import { getColorTokenValue } from '@utility-nyc/react-native-ui-config';
import { useMedia, YStack } from 'tamagui';
import { useShallow } from 'zustand/react/shallow';

import { HeadingXL } from '@south-street-app/atoms';
import { en_US } from '@south-street-app/configs';
import { useRecentTradeMatch } from '@south-street-app/hooks';
import {
  useMobileTradeStore,
} from '@south-street-app/stores';
import { useGetCurrentEndUser } from '@utility-nyc/react-query-sdk';

import { FirmMultiplePreviewTradeOrder } from './FirmMultiplePreviewTradeOrder';
import { FirmSinglePreviewOrder } from './FirmSinglePreviewOrder';
import { MultiplePreviewTradeOrder } from './MultiplePreviewTradeOrder';
import { SinglePreviewTradeOrder } from './SinglePreviewTradeOrder';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: getColorTokenValue('$mono100'),
  },
});

const PreviewTradeOrderScreen = () => {
  const { data: currentUser } = useGetCurrentEndUser();
  const { proposal, shares, tradeType } = useMobileTradeStore(
    useShallow((state) => ({
      proposal: state.proposal,
      shares: state.shares,
      tradeType: state.tradeType,
    })),
  );
  const components = proposal.components.map(({ product, amount, type }) => ({
    productId: product.id,
    amount,
    type,
  }));
  const candidateShares = shares.length
    ? shares
    : components.map(({ amount, type }) => ({
        amount,
        type,
        associatedId: currentUser?.data.id ?? '',
      }));
  const recentTradeMatch = useRecentTradeMatch(
    currentUser
      ? { components, shares: candidateShares, type: tradeType }
      : null,
  );
  const isTradeStateUnavailable =
    recentTradeMatch.isChecking || recentTradeMatch.isError || !currentUser;
  const isMatchingRecentTrade =
    !!recentTradeMatch.tradeRequestId ||
    (recentTradeMatch.retryAfterMs ?? 0) > 0;
  const { desktop } = useMedia();

  return (
    <SafeAreaView style={styles.container}>
      <YStack
        space={'$7'}
        paddingHorizontal={'$4'}
        paddingVertical={desktop ? '$4' : '$7'}
        flex={1}
        {...(desktop && {
          width: 800,
          marginHorizontal: '$auto',
          justifyContent: 'flex-start',
        })}
      >
        <HeadingXL fontWeight={'$3'}>{en_US.confirmTrade}</HeadingXL>

        {proposal.components.length === 1 ? (
          shares.length >= 1 ? (
            <FirmSinglePreviewOrder
              isTradeStateUnavailable={isTradeStateUnavailable}
              isMatchingRecentTrade={isMatchingRecentTrade}
            />
          ) : (
            <SinglePreviewTradeOrder
              isTradeStateUnavailable={isTradeStateUnavailable}
              isMatchingRecentTrade={isMatchingRecentTrade}
            />
          )
        ) : shares.length >= 1 ? (
          <FirmMultiplePreviewTradeOrder
            isTradeStateUnavailable={isTradeStateUnavailable}
            isMatchingRecentTrade={isMatchingRecentTrade}
          />
        ) : (
          <MultiplePreviewTradeOrder
            isTradeStateUnavailable={isTradeStateUnavailable}
            isMatchingRecentTrade={isMatchingRecentTrade}
          />
        )}
      </YStack>
    </SafeAreaView>
  );
};

export { PreviewTradeOrderScreen };
